// Shared math + drawing helpers for the morph-selector prototypes.
//
// The morph parameter is a single float in [0, 3] indexing four archetypes:
//   0 = sine, 1 = triangle, 2 = square, 3 = saw.
// At integer values the output equals the archetype exactly; in between, the
// sample is the linear lerp of the two neighbouring archetypes — so the
// shape continuously deforms as the user drags between any two of them.

const TAU = Math.PI * 2;

export const MORPH_MIN = 0;
export const MORPH_MAX = 3;

export const MORPH_NAMES = ["sine", "triangle", "square", "saw"] as const;
export type MorphArchetype = (typeof MORPH_NAMES)[number];

/** Sample one of the four archetypes at phase `t` ∈ [0, 1). */
function archetypeSample(idx: number, t: number): number {
  if (idx <= 0) return Math.sin(t * TAU);
  if (idx === 1) {
    // Triangle phase-aligned with sine — zero at 0, +1 at 0.25, 0 at 0.5,
    // −1 at 0.75. Makes the sine→triangle lerp visually smooth.
    if (t < 0.25) return 4 * t;
    if (t < 0.75) return 2 - 4 * t;
    return 4 * t - 4;
  }
  if (idx === 2) return t < 0.5 ? 1 : -1;
  return 2 * t - 1; // saw: −1 → +1 across the period
}

/** Morphed waveform sample at phase `t` ∈ [0, 1), morph clamped to [0, 3]. */
export function morphedSample(t: number, morph: number): number {
  const m = Math.max(MORPH_MIN, Math.min(MORPH_MAX, morph));
  const idx = Math.floor(m);
  const frac = m - idx;
  const phase = t - Math.floor(t);
  const a = archetypeSample(idx, phase);
  if (frac === 0 || idx >= 3) return a;
  const b = archetypeSample(idx + 1, phase);
  return a + (b - a) * frac;
}

export interface MorphPathOptions {
  width: number;
  height: number;
  /** Vertical amplitude as fraction of height/2. Default 0.78. */
  amplitudeFrac?: number;
  /** Number of full cycles drawn across `width`. Default 1.5. */
  cycles?: number;
  /** Phase offset in [0, 1). Default 0. */
  phaseOffset?: number;
  /** Origin x (default 0). */
  originX?: number;
  /** Origin y (vertical centre). Default height/2. */
  centerY?: number;
}

/** Trace the morphed waveform as a path into the supplied context. The
 *  caller controls stroke/fill style and the surrounding clear. */
export function pathMorphedWave(
  ctx: CanvasRenderingContext2D,
  morph: number,
  opts: MorphPathOptions,
): void {
  const w = opts.width;
  const h = opts.height;
  const ampFrac = opts.amplitudeFrac ?? 0.78;
  const cycles = opts.cycles ?? 1.5;
  const phase0 = opts.phaseOffset ?? 0;
  const ox = opts.originX ?? 0;
  const cy = opts.centerY ?? h / 2;
  const amp = (h / 2) * ampFrac;

  // Two samples per CSS pixel keeps square-wave transitions to a 1-px wide
  // diagonal — visually indistinguishable from a true vertical edge at the
  // sizes we draw at, without needing discontinuity detection.
  const steps = Math.max(1, Math.floor(w * 2));
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const x = ox + (i / steps) * w;
    const t = phase0 + (i / steps) * cycles;
    const y = cy - morphedSample(t, morph) * amp;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
}

/** Shared phase clock — a slowly advancing phase in [0, 1) for the live
 *  preview animations. Exposed as a function so each consumer can read the
 *  same phase on the same frame, keeping the four prototypes synchronised
 *  in the demo. Default rate is intentionally lazy (~3.3 second period)
 *  so the preview reads as a calm, ambient indicator rather than a
 *  twitchy waveform-of-the-week. */
export function sharedPhase(now: number, hz = 0.3): number {
  const period = 1000 / hz;
  return ((now % period) / period + 1) % 1;
}

/** Format `morph` as a "blend" string for readouts. At integer values it
 *  returns the archetype name; in between it shows the dominant archetype
 *  with the mix percentage. */
export function formatMorph(m: number): string {
  const clamped = Math.max(MORPH_MIN, Math.min(MORPH_MAX, m));
  const idx = Math.round(clamped);
  if (Math.abs(clamped - idx) < 0.02) return MORPH_NAMES[idx]!;
  const lo = Math.floor(clamped);
  const hi = lo + 1;
  const frac = clamped - lo;
  const dominantIdx = frac < 0.5 ? lo : hi;
  const dominant = MORPH_NAMES[dominantIdx]!;
  const blend = Math.round((frac < 0.5 ? 1 - frac : frac) * 100);
  return `${dominant} ${blend}%`;
}
